import React, { useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { decodeHtml } from "../data/helper";

export interface StepItem {
  id: string;
  name: string;
}

interface SectionStepperProps {
  sections: StepItem[];
  currentIndex: number;
  onSelect?: (index: number) => void;
  className?: string;
}

export function SectionStepper({ sections, currentIndex, className }: SectionStepperProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Find the active step item
    const activeEl = container.querySelector<HTMLDivElement>(`[data-step-index="${currentIndex}"]`);
    if (!activeEl) return;
    // Smoothly center the active item in the scroll container
    const scrollEl = container; // horizontal scroll container
    const targetLeft = activeEl.offsetLeft - scrollEl.clientWidth / 2 + activeEl.offsetWidth / 2;
    const maxLeft = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    const clamped = Math.min(Math.max(0, targetLeft), maxLeft);
    scrollEl.scrollTo({ left: clamped, behavior: "smooth" });
  }, [currentIndex, sections.length]);

  return (
    <div ref={containerRef} className={`shrink-0 bg-white border-b border-gray-200 px-6 py-4 overflow-x-auto no-scrollbar ${className ?? ""}`}>
      <div className="flex items-center gap-2 min-w-max">
        {sections.map((section, idx) => (
          <React.Fragment key={section.id}>
            <div
              data-step-index={idx}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${idx === currentIndex
                ? "bg-primary text-on-primary"
                : idx < currentIndex
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-500"
                }`}
            >
              <div
                className={`flex items-center justify-center w-5 h-5 rounded-full text-xs ${idx === currentIndex
                  ? "bg-white text-gray-900"
                  : idx < currentIndex
                    ? "bg-green-600 text-white"
                    : "bg-gray-300 text-gray-600"
                  }`}
              >
                {idx < currentIndex ? (
                  <Check className="w-3 h-3" />
                ) : (
                  idx + 1
                )}
              </div>
              <span className="text-xs font-normal whitespace-nowrap uppercase">
                {decodeHtml(section.name)}
              </span>
            </div>
            {idx < sections.length - 1 && (
              <div className="w-8 h-px bg-gray-300" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default SectionStepper;
